import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ProductSearchPicker } from '@/components/po/ProductSearchPicker'
import {
  fetchVariantPrice,
  useAddOfflineSale,
  useDeleteOfflineSale,
  useOfflineSales,
  useUpdateOfflineSale,
  type OfflineSale,
  type OfflineSaleCreate,
  type OfflineSaleUpdate,
} from '@/hooks/useEvents'
import { ApiError } from '@/lib/api/rawFetch'
import { fmtDate } from '@/lib/dates'
import { useAuthStore } from '@/store/authStore'

// Sales made outside POS at an event (Shopify draft orders on the main site,
// placed at the show). Kept as individual entries so each one is traceable,
// and flagged "Offline" everywhere so they never read as POS revenue.

const money2 = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })

// Cost is required (blank ≠ 0): a deliberate $0 is fine, a forgotten cost
// would silently count the sale at 100% margin.
// Every new sale names what was sold — a catalog variant or, for something not
// in the catalog, a product type — so product analysis and the pack list can
// count it. `allow_unpicked` is set only when editing an entry from before
// products were tracked, which may be saved without classifying it yet.
const schema = z
  .object({
    kind: z.enum(['catalog', 'custom']),
    variant_id: z.string(),
    product_type: z.string(),
    allow_unpicked: z.boolean(),
    qty: z.string().refine((v) => /^\d+$/.test(v.trim()) && Number(v) >= 1, {
      message: 'Enter a whole quantity of 1 or more',
    }),
    amount: z.string().refine((v) => v.trim() !== '' && Number(v) > 0, {
      message: 'Enter an amount greater than 0',
    }),
    cost: z
      .string()
      .refine((v) => v.trim() !== '', { message: 'Enter a cost — type 0 if there was none' })
      .refine((v) => v.trim() === '' || (Number.isFinite(Number(v)) && Number(v) >= 0), {
        message: 'Cost must be ≥ 0',
      }),
    sale_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date'),
    note: z
      .string()
      .refine((v) => v.trim() !== '', { message: 'Add a note — who bought it, draft order #' })
      .refine((v) => v.trim().length <= 500, { message: 'Note must be 500 characters or fewer' }),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'catalog' && v.variant_id === '' && !v.allow_unpicked) {
      ctx.addIssue({ code: 'custom', path: ['variant_id'], message: 'Pick the product that was sold' })
    }
    if (v.kind === 'custom' && v.product_type === '') {
      ctx.addIssue({ code: 'custom', path: ['product_type'], message: 'Pick a product type' })
    }
  })

type Values = z.infer<typeof schema>

const money2Plain = (v: number) => (Math.round(v * 100) / 100).toFixed(2)

function emptyValues(): Values {
  return {
    kind: 'catalog', variant_id: '', product_type: '', allow_unpicked: false,
    qty: '1', amount: '', cost: '', sale_date: todayIso(), note: '',
  }
}

/** What the list row says was sold. */
function soldLabel(s: OfflineSale): string | null {
  if (s.kind === 'product') {
    const parts = [s.product_title, s.variant_title, s.sku].filter(Boolean).join(' · ')
    return `${parts} × ${s.qty ?? 1}`
  }
  if (s.kind === 'custom') return `Custom · ${s.product_type} × ${s.qty ?? 1}`
  return null
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// FastAPI 422s carry `detail` as an array of {msg}; apiFetch only lifts a
// string detail into the message, so pull the first msg out here.
function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const d = (err.detail as { detail?: unknown } | undefined)?.detail
    if (Array.isArray(d) && typeof d[0]?.msg === 'string') return d[0].msg
  }
  return err instanceof Error ? err.message : fallback
}

export function OfflineSalesPanel({
  eventId,
  productTypes,
}: {
  eventId: number
  /** Options for a custom item's product type — the Analysis filter's list. */
  productTypes: string[]
}) {
  const { data: sales = [], isLoading } = useOfflineSales(eventId)
  const deleteSale = useDeleteOfflineSale()
  const user = useAuthStore((s) => s.user)
  // Same gate as deleting a product request: only Gina / admins. The server
  // enforces it too — this just hides a button that would 403.
  const canDelete =
    user != null && (user.username?.toLowerCase() === 'gina' || user.role === 'admin')

  // null = closed, 'new' = add, an entry = edit that entry.
  const [editing, setEditing] = React.useState<OfflineSale | 'new' | null>(null)

  async function onDelete(s: OfflineSale) {
    if (!window.confirm(`Delete the ${money2(s.amount)} offline sale "${s.note}"?`)) return
    try {
      await deleteSale.mutateAsync({ eventId, saleId: s.id })
      toast.success('Offline sale deleted')
    } catch (err) {
      toast.error(errMessage(err, 'Delete failed'))
    }
  }

  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] mt-2" data-testid="offline-sales-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--line-warm)] bg-[var(--surface-header)]">
        <h2 className="text-xs font-semibold">Offline sales</h2>
        <span className="text-xs text-[var(--mute)]">Sales made outside POS, e.g. draft orders</span>
        <div className="flex-1" />
        <Button size="sm" className="h-7 text-xs" onClick={() => setEditing('new')}>
          <Plus className="mr-1 h-3 w-3" />
          Add offline sale
        </Button>
      </div>
      {isLoading ? null : sales.length === 0 ? (
        <div className="px-3 py-3 text-xs text-[var(--mute)]">
          No offline sales recorded for this event.
        </div>
      ) : (
        <ul className="text-xs">
          {sales.map((s) => (
            <li
              key={s.id}
              className="flex items-baseline gap-3 px-3 py-1.5 border-b border-[var(--line-warm)] last:border-b-0 flex-wrap"
              data-testid="offline-sale-row"
            >
              <span className="inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]">
                Offline
              </span>
              {s.kind === 'unclassified' && (
                <span
                  className="inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]"
                  title="Edit this sale and pick what was sold so it counts in product analysis"
                >
                  Needs product
                </span>
              )}
              <span className="tabular-nums whitespace-nowrap">{fmtDate(s.sale_date)}</span>
              <span className="tabular-nums">
                <strong>{money2(s.amount)}</strong>
                <span className="text-[var(--mute)]"> · cost {money2(s.cost)}</span>
              </span>
              <span className="flex-1 min-w-[12rem] text-[var(--ink-2)]">
                {soldLabel(s) && <span className="text-[var(--text-primary)]">{soldLabel(s)} — </span>}
                {s.note}
              </span>
              <span className="text-[var(--mute)]">
                added by {s.created_by}
                {s.updated_by && ` · edited by ${s.updated_by}`}
              </span>
              <button
                type="button"
                className="text-[var(--mute)] hover:text-[var(--text-primary)]"
                aria-label={`Edit offline sale ${s.note}`}
                onClick={() => setEditing(s)}
              >
                <Pencil className="h-3 w-3" />
              </button>
              {canDelete && (
                <button
                  type="button"
                  className="text-[var(--mute)] hover:text-[var(--destructive)]"
                  aria-label={`Delete offline sale ${s.note}`}
                  disabled={deleteSale.isPending}
                  onClick={() => onDelete(s)}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <OfflineSaleDialog
        eventId={eventId}
        sale={editing === 'new' ? null : editing}
        productTypes={productTypes}
        open={editing != null}
        onOpenChange={(o) => !o && setEditing(null)}
      />
    </div>
  )
}

function OfflineSaleDialog({
  eventId,
  sale,
  productTypes,
  open,
  onOpenChange,
}: {
  eventId: number
  sale: OfflineSale | null
  productTypes: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const addSale = useAddOfflineSale()
  const updateSale = useUpdateOfflineSale()
  const busy = addSale.isPending || updateSale.isPending

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues(),
  })

  // The picked variant, for display only — the form carries its id.
  const [picked, setPicked] = React.useState<{
    product_title: string
    variant_title: string
    sku: string
  } | null>(null)
  // Per-unit price/cost from the picked variant, used to prefill Amount and
  // Cost as unit × qty — but only until someone types in that field: the
  // actual show price is often a deal, and it must never be overwritten.
  const unit = React.useRef<{ price: number; cost: number }>({ price: 0, cost: 0 })
  const touched = React.useRef({ amount: false, cost: false })

  React.useEffect(() => {
    if (open) {
      unit.current = { price: 0, cost: 0 }
      // An existing entry's amounts are what was actually charged — never
      // recompute them.
      touched.current = { amount: sale != null, cost: sale != null }
      setPicked(
        sale?.kind === 'product'
          ? {
              product_title: sale.product_title ?? '',
              variant_title: sale.variant_title ?? '',
              sku: sale.sku ?? '',
            }
          : null
      )
      form.reset(
        sale
          ? {
              kind: sale.kind === 'custom' ? 'custom' : 'catalog',
              variant_id: sale.kind === 'product' && sale.variant_id != null ? String(sale.variant_id) : '',
              product_type: sale.kind === 'custom' ? sale.product_type ?? '' : '',
              allow_unpicked: sale.kind === 'unclassified',
              qty: String(sale.qty ?? 1),
              amount: String(sale.amount),
              cost: String(sale.cost),
              sale_date: sale.sale_date,
              note: sale.note,
            }
          : emptyValues()
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sale])

  function prefill() {
    const qty = Number(form.getValues('qty'))
    if (!Number.isInteger(qty) || qty < 1) return
    if (!touched.current.amount && unit.current.price > 0) {
      form.setValue('amount', money2Plain(unit.current.price * qty))
    }
    if (!touched.current.cost && unit.current.cost > 0) {
      form.setValue('cost', money2Plain(unit.current.cost * qty))
    }
  }

  const kind = form.watch('kind')
  // An existing custom entry keeps its type selectable even if it has since
  // dropped out of the filter list.
  const typeOptions =
    sale?.kind === 'custom' && sale.product_type && !productTypes.includes(sale.product_type)
      ? [sale.product_type, ...productTypes]
      : productTypes

  async function onSubmit(values: Values) {
    const fields = {
      amount: Number(values.amount),
      cost: Number(values.cost),
      sale_date: values.sale_date,
      note: values.note.trim(),
      qty: Number(values.qty),
    }
    // Exactly one of variant_id / product_type — or neither when an
    // unclassified entry is saved without picking. Titles are never sent;
    // the server fills them from the variant.
    const body: OfflineSaleUpdate =
      values.kind === 'custom'
        ? { ...fields, product_type: values.product_type }
        : values.variant_id !== ''
          ? { ...fields, variant_id: Number(values.variant_id) }
          : fields
    try {
      if (sale) {
        await updateSale.mutateAsync({ eventId, saleId: sale.id, body })
        toast.success('Offline sale updated')
      } else {
        // The schema guarantees a new entry has a variant or a type.
        await addSale.mutateAsync({ eventId, body: body as OfflineSaleCreate })
        toast.success('Offline sale added')
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(errMessage(err, 'Failed to save offline sale'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{sale ? 'Edit offline sale' : 'Add offline sale'}</DialogTitle>
          <DialogDescription>
            A sale made at this event outside POS. It counts toward the event&apos;s revenue,
            shown separately from POS.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex min-w-0 flex-col gap-3">
            <div role="radiogroup" aria-label="What was sold" className="flex gap-4 text-xs">
              {(
                [
                  ['catalog', 'Catalog product'],
                  ['custom', 'Custom item (not in catalog)'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="offline-sale-kind"
                    checked={kind === value}
                    disabled={busy}
                    onChange={() => {
                      form.setValue('kind', value)
                      form.clearErrors(['variant_id', 'product_type'])
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            {kind === 'catalog' ? (
              <FormField
                control={form.control}
                name="variant_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product</FormLabel>
                    {field.value !== '' && picked ? (
                      <div className="flex items-center gap-2 border border-[var(--line-warm)] bg-[var(--surface-page)] px-3 py-2 text-xs">
                        <div className="min-w-0 flex-1" data-testid="offline-sale-picked">
                          <div className="break-words font-medium text-[var(--text-primary)]">{picked.product_title}</div>
                          <div className="break-all text-[var(--mute)]">
                            {picked.variant_title || '—'}
                            {picked.sku ? ` · SKU ${picked.sku}` : ''}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={busy}
                          onClick={() => {
                            setPicked(null)
                            field.onChange('')
                          }}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <ProductSearchPicker
                        disabled={busy}
                        onPick={(item) => {
                          setPicked({
                            product_title: item.product_title,
                            variant_title: item.variant_title ?? '',
                            sku: item.sku ?? '',
                          })
                          field.onChange(String(item.variant_id))
                          // The picker's rows carry cost but no price, so the
                          // price arrives separately; until then (or if the
                          // lookup fails) Amount stays blank for the user.
                          unit.current = { price: 0, cost: item.cost_base }
                          prefill()
                          const variantId = String(item.variant_id)
                          fetchVariantPrice(item.variant_id)
                            .then(({ price }) => {
                              // Stale answer: another product was picked, or the
                              // dialog was closed, while this was in flight.
                              if (form.getValues('variant_id') !== variantId) return
                              unit.current.price = price
                              prefill()
                            })
                            .catch((err) => console.error('Variant price lookup failed', err))
                        }}
                      />
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="product_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product type</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        disabled={busy}
                        className="h-9 border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs focus:outline-none focus:border-[var(--brand)]"
                      >
                        <option value="">Pick a product type…</option>
                        {typeOptions.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="qty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Qty</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        prefill()
                      }}
                      disabled={busy}
                      className="w-24"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        {...field}
                        onChange={(e) => {
                          touched.current.amount = true
                          field.onChange(e)
                        }}
                        disabled={busy}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        {...field}
                        onChange={(e) => {
                          touched.current.cost = true
                          field.onChange(e)
                        }}
                        disabled={busy}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="sale_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} disabled={busy} className="w-44" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note/Customer Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={busy}
                      maxLength={500}
                      placeholder="Jane Smith custom order, draft order #4412"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy} data-testid="offline-sale-submit">
                {busy ? 'Saving…' : sale ? 'Save changes' : 'Add sale'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
